import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormikValues } from 'formik';

import * as actionTypes from 'store/action-types';
import { State } from 'shared/interface';
import AuthService from 'shared/services/auth.service';
import httpService from 'shared/services/http.service';
import LoginWrapper from 'shared/hoc/auth/loginWrapper';
import { CustomForm } from 'shared/components/form/form';
import { refreshLanguage } from 'shared/util/localization';
import * as schema from 'shared/constants/validation-schema';
import { FieldConfig } from 'shared/components/form/inputTypes';
import localizationConstants from 'shared/util/translation/constants';
import { createAction, createLoadingSelector } from 'shared/util/utility';
import { API_CONFIG } from 'shared/constants/constants';
import { LoginResponse, UserProfileResponse } from '../interface/login.interface';
import { toast } from 'react-toastify';
import MenuService from 'shared/services/menu.service';

declare global {
	interface Window {
		google: any;
	}
}

const LoginComponent: React.FC = () => {
	const [isLoggedIn, setIsLoggedIn] = useState(false)
	const dispatch = useDispatch();
	const loading = useSelector((state: State) => loadingSelector(state));

	const onLogin = (values: FormikValues, formikHelpers: any) => {
		dispatch(createAction(actionTypes.AUTH_INIT));
		httpService.post(API_CONFIG.path.magicLogin, { email: values.email }, {}, {
			isAccessTokenRequire: false
		})
		.then((response) => {
			if (response && response.message) {
				toast.info(response.message);
			}
			dispatch(createAction(actionTypes.AUTH_STOP_LOADING));
			formikHelpers.resetForm();
		})
		.catch((error) => {
			dispatch(createAction(actionTypes.AUTH_FAIL));
		});
	}

	const onGoogleLogin = (googleToken: string) => {
		dispatch(createAction(actionTypes.AUTH_INIT));

		httpService.post(API_CONFIG.path.googleLogin, { token: googleToken }, {}, {
			isAccessTokenRequire: false
		})
		.then((response: LoginResponse) => {
			AuthService.setAuthData(response);

			httpService.get(API_CONFIG.path.profile).then((profileResponse: UserProfileResponse) => {
				profileResponse.sidebarMenu = MenuService.generateSidebarMenu(profileResponse);
				AuthService.setUserData(profileResponse);
				refreshLanguage(profileResponse.user.language || 'de');
				dispatch(createAction(actionTypes.AUTH_SUCCESS, profileResponse));
				setTimeout(() => setIsLoggedIn(true), 500);
			});
		})
		.catch(() => {
			dispatch(createAction(actionTypes.AUTH_FAIL));
		});
	};

	useEffect(() => {
		if (typeof window.google === 'undefined') {
			console.warn('Google Sign-In script not loaded yet');
			return;
		}

		window.google.accounts.id.initialize({
			client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
			callback: handleCredentialResponse,
		});

		window.google.accounts.id.renderButton(
			document.getElementById('googleSignInDiv'),
			{ theme: 'outline', size: 'large', width: 300 }
		);

		window.google.accounts.id.prompt();
	}, []);

	const handleCredentialResponse = (response: { credential: string }) => {
		onGoogleLogin(response.credential);
	};

	if (isLoggedIn) {
		return <Navigate to={"/"} />
	}

	return (
		<LoginWrapper>
			<CustomForm
				showLabels
				title={localizationConstants.login}
				formClassName='m-t'
				schema={schema.magicLoginFormValidationSchema}
				onSubmit={onLogin}
				loading={loading}
				fieldConfig={fieldConfig}
				submitBtnText={localizationConstants.sendMagicLink}
				submitBtnClassName='block full-width m-b'
			/>
			<div id="googleSignInDiv" style={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
		</LoginWrapper>
	)
}
const loadingSelector = createLoadingSelector(['AUTH']);

const fieldConfig: FieldConfig[] = [
	{ type: 'email', label: localizationConstants.email, name: 'email' },
];

export default LoginComponent;
